import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import NoDataMsg from "../info/NoDataMsg";
import { TbTemplateOff } from "react-icons/tb";
import DragAndDropUploaderFiles from "../forms/stepper/components/dragAndDrop/DragAndDropUploaderFiles";
import FormDialog from "../dialog/FormDialog";
import RoundedButton from "../buttons/RoundedButton";
import { useState, useEffect } from "react";
import { FaExclamationTriangle } from 'react-icons/fa';
import Spinner from "../Spinner";
import CollapsibleSection from "../CollapsibleSection";
import { TbPrompt, TbInfoCircleFilled } from "react-icons/tb";
import { FaCheckCircle } from "react-icons/fa";
import AlertMsg from "../info/AlertMsg";
import { FaDownload } from "react-icons/fa";
import { Button } from "primereact/button";

const TEMPLATE_STATUS_LABELS = {
    APPROVED: {
        label: "Aprobada",
        styles: {
            borderColor: "#22a06b",
            color: "#22a06b",
            backgroundColor: "#22a06b17",
        },
        icon: "pi pi-check"
    },
    PENDING: {
        label: "En revisión",
        styles: {
            borderColor: "#3e82da",
            color: "#3e82da",
            backgroundColor: "#3e82da15",
        },
        icon: "pi pi-clock"
    },
    REJECTED: {
        label: "Rechazada",
        styles: {
            borderColor: "#e5533d",
            color: "#e5533d",
            backgroundColor: "#e5533d17",
        },
        icon: "pi pi-times"
    },
};

const MultiStepForm = ({
    fileInputList,
    setFileInputList,
    trackerData = [],
    saveTemplate
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [jsonContent, setJsonContent] = useState(null);

    useEffect(() => {
        if (fileInputList.length !== 1) {
            setJsonContent(null);
            return;
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                setJsonContent(JSON.parse(e.target.result));
            } catch {
                setJsonContent({ error: "JSON inválido" });
            }
        };

        reader.readAsText(fileInputList[0]);
    }, [fileInputList]);

    const handleCreate = async () => {
        if (!fileInputList.length) return;

        setIsLoading(true);

        const success = await saveTemplate();

        setIsLoading(false);

        if (success) {
            setStep(2);
        }
    };

    if (isLoading) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "60vh" }}
            >
                <Spinner message="Cargando, este proceso puede tardar unos minutos..." />
            </div>
        );
    }

    if (step === 2) {
        const hasErrors = trackerData?.some(t => t.status === "error");

        return (
            <div className="mt-4 mb-3">
                <div className="text-center mb-3">
                    <FaCheckCircle
                        size={50}
                        color={hasErrors ? "#F59E0B" : "green"}
                        className="mb-3"
                    />
                    <h5>
                        {hasErrors
                            ? "Proceso finalizado con observaciones"
                            : "¡Creación exitosa!"}
                    </h5>
                    <p>
                        {hasErrors
                            ? "Algunas plantillas tuvieron errores."
                            : "Todas las plantillas fueron enviadas correctamente."}
                    </p>
                </div>

                {trackerData?.length > 0 && (
                    <CollapsibleSection
                        title="Detalle del procesamiento"
                        initialShowState={true}
                    >
                        <div className="d-flex flex-column gap-2">
                            {trackerData.map((item, index) => {
                                const isError = item.status === "error";
                                return (
                                    <div
                                        key={index}
                                        className="d-flex flex-column p-2"
                                        style={{
                                            borderRadius: "8px",
                                            border: `1px solid ${isError ? "#e5533d" : "#22a06b"}`,
                                            backgroundColor: isError ? "#e5533d10" : "#22a06b10"
                                        }}
                                    >
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                            <span style={{ fontWeight: 500 }}>
                                                {item.bot_id} - {item.bot_name || "-"}
                                            </span>

                                            <span
                                                style={{
                                                    fontSize: "12px",
                                                    padding: "2px 8px",
                                                    borderRadius: "999px",
                                                    backgroundColor: isError ? "#e5533d17" : "#22a06b17",
                                                    color: isError ? "#e5533d" : "#22a06b",
                                                    border: `1px solid ${isError ? "#e5533d" : "#22a06b"}`
                                                }}
                                            >
                                                {isError ? "ERROR" : "OK"}
                                            </span>
                                        </div>
                                        {isError && item.error_msg && (
                                            <span
                                                style={{
                                                    fontSize: "12px",
                                                    color: "#e5533d",
                                                    marginTop: "4px"
                                                }}
                                            >
                                                {item.error_msg}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </CollapsibleSection>
                )}
            </div>
        );
    }

    return (
        <>
            {fileInputList.length > 1 && (
                <AlertMsg
                    type="danger"
                    text="Solo se admite la creación mediante un único archivo JSON."
                />
            )}

            <div className="mt-3">
                {fileInputList.length > 0 && <label>Archivo seleccionado:</label>}

                <DragAndDropUploaderFiles
                    setFileInputList={setFileInputList}
                    fileInputList={fileInputList}
                    maxFileSize={1}
                    acceptedExtensions={[".json"]}
                />
            </div>

            {fileInputList.length === 1 && jsonContent && (
                <div className="mt-2 d-flex flex-column gap-2">

                    <CollapsibleSection
                        title="Información general"
                        initialShowState={true}
                        icon={<TbInfoCircleFilled />}
                    >
                        <div className="ms-2 d-flex flex-column">
                            <label>Nombre: <b>{jsonContent.name || "-"}</b></label>
                            <label>Categoría: <b>{jsonContent.category || "-"}</b></label>
                        </div>
                    </CollapsibleSection>

                    <CollapsibleSection
                        title="Contenido"
                        initialShowState={true}
                        icon={<TbPrompt />}
                    >
                        <pre
                            style={{
                                padding: "12px",
                                borderRadius: "8px",
                                overflowX: "auto",
                                fontSize: "12px"
                            }}
                        >
                            {JSON.stringify(jsonContent, null, 2)}
                        </pre>
                    </CollapsibleSection>

                </div>
            )}

            <div className="d-flex justify-content-center mt-3">
                <button
                    type="button"
                    className="primaryBtn"
                    onClick={handleCreate}
                    style={{ width: "200px" }}
                >
                    Crear
                </button>
            </div>
        </>
    );
};

const downloadTemplate = (row) => {
    const json = JSON.stringify(row.downloadable_json, null, 2); // template del backend

    const blob = new Blob([json], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${row.name}.json`;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const TableBotsTemplates = ({
    data,
    selectedTemplateFiles,
    setSelectedTemplateFiles,
    addTemplate,
    trackerData
}) => {
    if (!data || data.length === 0) {
        return (
            <div className="card p-3">
                <NoDataMsg
                    title="Sin datos que mostrar"
                    subtitle="No se han podido cardar la informacion de las plantillas."
                    icon={<TbTemplateOff />}
                />
            </div>
        );
    }

    const [showAddTemplateDialog, setShowAddTemplateDialog] = useState(false);

    return (
        <div className="card" aria-label="table">
            <DataTable
                value={data}
                paginator
                header={
                    <div className="d-flex justify-content-between align-items-center m-1">
                        <h4> <b>Plantillas de WhatsApp</b> </h4>
                        <RoundedButton label="+ Agregar plantilla" handleClick={() => { setShowAddTemplateDialog(true); }} />
                    </div>
                }
                rows={50}
                rowsPerPageOptions={[5, 10, 25, 50]}
                filterDisplay="row"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                filter
                responsiveLayout="scroll"
                aria-label="some"
            >
                <Column
                    field={"bot_id"}
                    header={"Chatbot ID"}
                    sortable
                    filter
                    filterMatchMode={["contains"]}
                    showFilterMenu={false}
                    body={(row) => {
                        return row?.bot_id ?? "-";
                    }}
                    style={{ maxWidth: "8rem" }}
                />
                <Column
                    field={"bot_name"}
                    header={"Nombre"}
                    sortable
                    filter
                    filterMatchMode={["contains"]}
                    showFilterMenu={false}
                    body={(row) => {
                        return row?.bot_name ? row.bot_name : "-";
                    }}
                />

                <Column
                    field={"name"}
                    header={"Plantilla"}
                    sortable
                    filter
                    filterMatchMode={["contains"]}
                    showFilterMenu={false}
                    body={(row) => {
                        return row?.name ? row.name : "-";
                    }}
                />
                <Column
                    field="category"
                    header="Categoría"
                    sortable
                    filter
                    filterMatchMode="contains"
                    showFilterMenu={false}
                    body={(row) => {
                        const wasRecategorized =
                            row.previous_category !== null &&
                            row.previous_category !== undefined &&
                            row.category !== row.previous_category;

                        return (
                            <div
                                className="d-inline-flex align-items-center gap-1 px-3 py-1"
                                title={wasRecategorized ? `Antes: ${row.previous_category}` : null}
                                style={{
                                    cursor: "pointer",
                                    borderRadius: "999px",
                                    fontSize: "13px",
                                    maxWidth: "100%",
                                    backgroundColor: wasRecategorized ? "#c0a02217" : "#1058af15",
                                    color: wasRecategorized ? "#c4703c" : "#3e82da",
                                    border: wasRecategorized ? "1px solid #F59E0B" : "1px solid #3e82da",
                                }}
                            >
                                <span
                                    style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {row.category || "-"}
                                </span>

                                {wasRecategorized && (
                                    <FaExclamationTriangle
                                        style={{
                                            fontSize: "12px",
                                            color: "#F59E0B",
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                            </div>
                        );
                    }}
                />
                <Column
                    field={"status"}
                    className={"p-3"}
                    header="Estado"
                    showFilterMenu={false}
                    sortable
                    style={{ maxWidth: "15rem" }}
                    body={(row) => {
                        const statusConfig = TEMPLATE_STATUS_LABELS[row?.status];

                        if (!statusConfig) {
                            return <span style={{ color: "#6c757d", fontSize: "13px" }}>-</span>;
                        }

                        const { label, styles, icon } = statusConfig;

                        return (
                            <div
                                className="d-inline-flex align-items-center gap-1 px-3 py-1"
                                style={{
                                    borderRadius: "999px",
                                    fontSize: "13px",
                                    maxWidth: "100%",
                                    backgroundColor: styles.backgroundColor,
                                    color: styles.color,
                                    border: `1px solid ${styles.borderColor}`,
                                }}
                            >
                                <i
                                    className={icon}
                                    style={{
                                        fontSize: "12px",
                                        color: styles.borderColor,
                                        flexShrink: 0,
                                    }}
                                />

                                <span
                                    style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {label}
                                </span>
                            </div>
                        );
                    }}
                />
                <Column
                    header={<></>}
                    className={"actionheader p-2"}
                    exportable={false}
                    body={(row) => (
                        <Button
                            type="button"
                            icon={<FaDownload size={"0.9rem"} />}
                            className="action-body-btn action-body-btn-aqua"
                            tooltip="Descargar plantilla"
                            tooltipOptions={{ position: "top" }}
                            onClick={() => downloadTemplate(row)}
                            aria-label="descargar"
                        />
                    )}
                    style={{ width: "6rem", textAlign: "center" }}
                ></Column>
            </DataTable>

            <FormDialog
                title="Agregar plantilla"
                visible={showAddTemplateDialog}
                width="600px"
                footer={<></>}
                closeDialog={() => {
                    setShowAddTemplateDialog(false);
                    setSelectedTemplateFiles([]);
                }}
            >
                <MultiStepForm
                    fileInputList={selectedTemplateFiles}
                    setFileInputList={setSelectedTemplateFiles}
                    saveTemplate={addTemplate}
                    trackerData={trackerData}
                />
            </FormDialog>
        </div>
    );
};

export default TableBotsTemplates;
